import { supabase, getAccessToken } from './supabase';
import i18n from '../i18n';

type EmailTemplate = 'welcome' | 'group_invite' | 'trip_shared' | 'trip_reminder' | 'waitlist_confirmation' | 'beta_invite';

interface SendEmailParams {
  template: EmailTemplate;
  to: string;
  data?: Record<string, string>;
  anonymous?: boolean;
}

export async function sendEmail(params: SendEmailParams): Promise<boolean> {
  try {
    const headers: Record<string, string> = {};
    if (!params.anonymous) {
      const token = await getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: { template: params.template, to: params.to, data: params.data, language: i18n.language },
      headers,
    });

    if (error) {
      console.warn('Email send failed:', error.message);
      return false;
    }

    return data?.success === true;
  } catch (error) {
    console.warn('Email send failed:', error);
    return false;
  }
}

// Convenience functions — all fire-and-forget

export function sendWelcomeEmail(email: string, name?: string): void {
  sendEmail({
    template: 'welcome',
    to: email,
    data: { name: name || '' },
  }).catch(() => {});
}

export function sendGroupInviteEmail(inviteeEmail: string, inviterName: string): void {
  sendEmail({
    template: 'group_invite',
    to: inviteeEmail,
    data: { inviter_name: inviterName },
  }).catch(() => {});
}

export function sendTripSharedEmail(params: {
  clientEmail: string;
  clientName: string;
  agentName: string;
  agentCompany?: string;
  agentLogo?: string;
  tripTitle: string;
  tripDates?: string;
  shareUrl: string;
}): void {
  sendEmail({
    template: 'trip_shared',
    to: params.clientEmail,
    data: {
      client_name: params.clientName,
      agent_name: params.agentName,
      agent_company: params.agentCompany || '',
      agent_logo: params.agentLogo || '',
      trip_title: params.tripTitle,
      trip_dates: params.tripDates || '',
      share_url: params.shareUrl,
    },
  }).catch(() => {});
}

export function sendTripReminderEmail(params: {
  email: string;
  name: string;
  tripId: string;
  tripTitle: string;
  startDate: string;
  daysUntil: number;
  totalStops: number;
  totalDays: number;
}): void {
  sendEmail({
    template: 'trip_reminder',
    to: params.email,
    data: {
      name: params.name,
      trip_id: params.tripId,
      trip_title: params.tripTitle,
      start_date: params.startDate,
      days_until: String(params.daysUntil),
      total_stops: String(params.totalStops),
      total_days: String(params.totalDays),
    },
  }).catch(() => {});
}

export function sendWaitlistConfirmationEmail(email: string, unsubscribeToken: string): void {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://japan.tripgenesys.com';
  const unsubscribeUrl = `${siteUrl}/unsubscribe?token=${encodeURIComponent(unsubscribeToken)}`;
  sendEmail({
    template: 'waitlist_confirmation',
    to: email,
    data: { unsubscribe_url: unsubscribeUrl },
    anonymous: true,
  }).catch(() => {});
}
